import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { EventService } from '../../core/services/event.service';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EIDDESIGN } from 'src/app/app-const';
import { SIDEBAR_TYPE } from "../layouts.model";
import { AuthenticationService, MgUser } from 'src/app/core/services/auth.service';
import { filter, take } from 'rxjs';
import { AcademyService } from 'src/app/core/backend/academy/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-vertical',
  templateUrl: './vertical.component.html',
  styleUrls: ['./vertical.component.scss'],
  standalone: false
})

/**
 * Vertical component
 */
export class VerticalComponent implements OnInit {
  EIDDESIGN = EIDDESIGN;
  isCondensed = false;
  sidebartype = '';


  academySubscriptionEndDate?: string | null = null;
  academySubscriptionDaysRemaining?: number | null = null;
  academySubscriptionStatus?: string | null = null;
  showSubscriptionNearExpiryBanner = false;
  showSubscriptionExpiredBanner = false;
  nearExpiryThreshold = 7;
  canAddBranch = true;
  allowedBranches = 0;


  // Trial banner state
  isTrialAcademy = false;
  trialDaysRemaining: number = 0;

  isBranchMode = false;


  constructor(private router: Router, private eventService: EventService, modalService: NgbModal, private authService: AuthenticationService, private academyService: AcademyService
  ) {
    this.router.events.forEach((event: any) => {
      if (event instanceof NavigationEnd) {
        document.body.classList.remove('sidebar-enable');
        // close all open modals
        modalService?.dismissAll();
      }
    });
  }
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.sidebartype = SIDEBAR_TYPE;

    this.changeSidebar(this.sidebartype);

    document.body.setAttribute('data-layout', 'vertical');

    // Initialize theme from localStorage
    const theme = localStorage.getItem('theme-mode') || 'light';
    document.body.setAttribute('data-theme', theme);

    this.authService.identity().subscribe(acc => {
      this.isBranchMode = !!localStorage.getItem('impersonated_branch_id');
    });

    // Refresh academy subscription data whenever the user navigates back to
    // this page (e.g. after creating a new branch from the form).
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.loadMainAcademySubscription();
      });

    this.authService.identity().subscribe(acc => {
      if (acc) {
        this.checkTrialStatus(acc);
      }
    });

    this.loadMainAcademySubscription();
  }


  loadMainAcademySubscription(): void {
    const parentAcademyId =
      this.authService.getCurrentMainAcademyContextId()
      || (this.authService.currentUserValue?.academy_id ? Number(this.authService.currentUserValue.academy_id) : null);

    if (!parentAcademyId) return;

    this.academyService.apiSubscriptionAcademyGetAcademyDataGet({ academy_id: String(parentAcademyId) })
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          const payload = (res && (res.data ?? res)) || res || {};
          const endDate = payload?.subscription_end_date ?? payload?.subscription?.end_date ?? payload?.end_date ?? payload?.subscription_end;
          this.academySubscriptionStatus = payload?.subscription_status ?? payload?.status ?? payload?.subscription?.status ?? null;

          const allowed = Number(
            payload?.allowed_branches
            ?? payload?.branches_count
            ?? payload?.subscription?.allowed_branches
            ?? payload?.subscription?.branches_count
            ?? 0
          );
          const used = Number(
            payload?.active_branches_count
            ?? payload?.enabled_branches_count
            ?? payload?.used_branches_count
            ?? payload?.branches_active_count
            ?? 0
          );
          const canAddRaw = payload?.can_add_branch ?? payload?.allow_add_branch ?? payload?.can_add_new_branch;
          this.allowedBranches = Number.isFinite(allowed) && allowed > 0 ? allowed : 0;
          if (typeof canAddRaw === 'boolean') {
            this.canAddBranch = canAddRaw;
          } else if (this.allowedBranches > 0) {
            this.canAddBranch = used < this.allowedBranches;
          } else {
            this.canAddBranch = true;
          }

          if (endDate) {
            this.academySubscriptionEndDate = endDate;
            const days = this.getRemainingDays(endDate);
            this.academySubscriptionDaysRemaining = days;
            this.showSubscriptionExpiredBanner = days < 0 || (typeof this.academySubscriptionStatus === 'string' && this.academySubscriptionStatus.toLowerCase().includes('inactive'));
            this.showSubscriptionNearExpiryBanner = !this.showSubscriptionExpiredBanner && days >= 0 && days <= this.nearExpiryThreshold;

            // Dismissals are not persisted so alerts re-appear after reload
          } else {
            if (typeof this.academySubscriptionStatus === 'string' && this.academySubscriptionStatus.toLowerCase().includes('inactive')) {
              this.showSubscriptionExpiredBanner = true;
            }
          }
        },
        error: () => {
          // ignore errors silently for now
        }
      });
  }

  getRemainingDays(endDate?: string | null): number {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const now = new Date();
    return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  isMobile() {
    const ua = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua);
  }



  checkTrialStatus(user: MgUser | null): void {
    // From backend Academy model: public const SUBSCRIPTION_TYPE_TRIAL = 1;
    if (user?.academy?.subscription_type === 1) {
      this.isTrialAcademy = true;
      const days = user?.academy?.trial_days_remaining;
      this.trialDaysRemaining = (typeof days === 'number' && Number.isFinite(days)) ? days : 0;
    } else {
      this.isTrialAcademy = false;
      this.trialDaysRemaining = 0;
    }
  }

  /**
   * on settings button clicked from topbar
   */
  onSettingsButtonClicked() {
    document.body.classList.toggle('right-bar-enabled');
  }

  changeSidebar(value: any) {
    switch (value) {
      case "light":
        document.body.removeAttribute('data-sidebar-size');
        document.body.removeAttribute('data-layout-size');
        document.body.removeAttribute('data-keep-enlarged');
        document.body.classList.remove('vertical-collpsed');
        document.body.removeAttribute('data-layout-scrollable');
        break;
      case "compact":
        document.body.setAttribute('data-sidebar-size', 'small');
        document.body.removeAttribute('data-layout-size');
        document.body.removeAttribute('data-keep-enlarged');
        document.body.classList.remove('sidebar-enable');
        document.body.classList.remove('vertical-collpsed');
        document.body.removeAttribute('data-layout-scrollable');
        break;
      case "dark":
        document.body.removeAttribute('data-layout-size');
        document.body.removeAttribute('data-keep-enlarged');
        document.body.removeAttribute('data-sidebar-size');
        document.body.classList.remove('sidebar-enable');
        document.body.classList.remove('vertical-collpsed');
        document.body.removeAttribute('data-layout-scrollable');
        break;
      case "icon":
        document.body.classList.add('vertical-collpsed');
        document.body.removeAttribute('data-layout-size');
        document.body.setAttribute('data-keep-enlarged', "true");
        document.body.removeAttribute('data-layout-scrollable');
        break;
      case "colored":
        document.body.classList.remove('sidebar-enable');
        document.body.classList.remove('vertical-collpsed');
        document.body.setAttribute('data-sidebar', 'colored');
        document.body.removeAttribute('data-layout-size');
        document.body.removeAttribute('data-keep-enlarged');
        document.body.removeAttribute('data-layout-scrollable');
        document.body.removeAttribute('data-sidebar-size');
        break;
      default:
        break;
    }
  }

  /**
   * On mobile toggle button clicked
   */
  onToggleMobileMenu() {
    this.isCondensed = !this.isCondensed;
    document.body.classList.toggle('sidebar-enable');
    // document.body.classList.toggle('vertical-collpsed');

    if (window.screen.width <= 768) {
      document.body.classList.remove('vertical-collpsed');
    }
  }


}
